import React from 'react'
import { useTranslation } from 'react-i18next'
import Text from '../../../elements/Typo/Text'
import { CsvProfile } from '../types/csv.types'

interface Props {
    profile: CsvProfile
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function delimiterLabel(delimiter: string, t: (key: string) => string): string {
    const tabLiteral = String.raw`\t`
    switch (delimiter) {
        case ',':
            return `${t('Comma')} (,)`
        case ';':
            return `${t('Semicolon')} (;)`
        case '\t':
            return `${t('Tab')} (${tabLiteral})`
        case '|':
            return `${t('Pipe')} (|)`
        default:
            return delimiter
    }
}

const CsvSummary: React.FC<Props> = ({ profile }) => {
    const { t } = useTranslation()
    return (
        <div className="grid grid-cols-5 gap-3 mb-3">
            <div className="col-span-5">
                <Text size="sm" className="!text-slate-500">
                    {t('File')}
                </Text>
                <Text size="md" className="font-semibold truncate" title={profile.fileName}>
                    {profile.fileName}
                </Text>
            </div>
            <div>
                <Text size="sm" className="!text-slate-500">
                    {t('File Size')}
                </Text>
                <Text size="md" className="font-semibold">
                    {formatBytes(profile.fileSize)}
                </Text>
            </div>
            <div>
                <Text size="sm" className="!text-slate-500">
                    {t('Columns')}
                </Text>
                <Text size="md" className="font-semibold">
                    {profile.columns.length}
                </Text>
            </div>
            {profile.totalRows !== undefined && (
                <div>
                    <Text size="sm" className="!text-slate-500">
                        {t('Rows sampled')}
                    </Text>
                    <Text size="md" className="font-semibold">
                        {profile.totalRows}
                    </Text>
                </div>
            )}
            <div>
                <Text size="sm" className="!text-slate-500">
                    {t('Delimiter')}
                </Text>
                <Text size="md" className="font-semibold">
                    {delimiterLabel(profile.delimiter, t)}
                </Text>
            </div>
            {profile.duplicateColumnNames.length > 0 && (
                <div className="col-span-4 bg-red-50 border border-red-200 rounded p-2">
                    <Text size="sm" className="!text-red-700 font-semibold">
                        {t('Duplicate column names')}: {profile.duplicateColumnNames.join(', ')}
                    </Text>
                </div>
            )}
        </div>
    )
}

export default CsvSummary
